export type TrailPoint = {
  x: number;
  z: number;
  t: number;
};

export type Crossing = {
  x: number;
  z: number;
};

const EPSILON = 1e-8;

export function distance(a: Pick<TrailPoint, "x" | "z">, b: Pick<TrailPoint, "x" | "z">): number {
  return Math.hypot(a.x - b.x, a.z - b.z);
}

export function shouldRecordPoint(points: TrailPoint[], point: TrailPoint, minimumDistance = 0.18): boolean {
  const last = points.at(-1);
  return !last || distance(last, point) >= minimumDistance;
}

export function segmentIntersection(
  a: Pick<TrailPoint, "x" | "z">,
  b: Pick<TrailPoint, "x" | "z">,
  c: Pick<TrailPoint, "x" | "z">,
  d: Pick<TrailPoint, "x" | "z">,
): Crossing | null {
  const rX = b.x - a.x;
  const rZ = b.z - a.z;
  const sX = d.x - c.x;
  const sZ = d.z - c.z;
  const denominator = rX * sZ - rZ * sX;

  if (Math.abs(denominator) < EPSILON) return null;

  const cax = c.x - a.x;
  const caz = c.z - a.z;
  const t = (cax * sZ - caz * sX) / denominator;
  const u = (cax * rZ - caz * rX) / denominator;

  if (t <= EPSILON || t >= 1 - EPSILON || u <= EPSILON || u >= 1 - EPSILON) return null;

  return { x: a.x + t * rX, z: a.z + t * rZ };
}

export function detectNewestCrossing(points: TrailPoint[]): Crossing | null {
  if (points.length < 5) return null;

  const a = points[points.length - 2];
  const b = points[points.length - 1];

  for (let index = 0; index <= points.length - 4; index += 1) {
    const c = points[index];
    const d = points[index + 1];
    const crossing = segmentIntersection(a, b, c, d);
    if (crossing) return crossing;
  }
  return null;
}

export function erodeWeakTrail(
  points: TrailPoint[],
  center: Pick<TrailPoint, "x" | "z">,
  radius = 0.5,
): { points: TrailPoint[]; removed: number } {
  if (points.length <= 4) return { points, removed: 0 };

  const next = points.filter((point, index) => {
    if (index < 2 || index >= points.length - 1) return true;
    return distance(point, center) >= radius;
  });

  return { points: next, removed: points.length - next.length };
}
